import DesignButton from 'components/button/DesignButton';
import DesignDialog from 'components/dialog/DesignDialog';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from 'components/svgs';
import { GenericTable } from 'components/table/IdmTable';
import { PlannedRouteView } from 'core/graphql/CapacityPlanning/models';
import useAppDispatch from 'core/hooks/useAppDispatch';
import {
  formatSecondsAsHoursMinutes,
  secondsFromMidnightToHhMm,
} from 'core/utils/TimeSpanUtils';
import { setCoordinates, setZoomLevel } from 'pages/itinerary/MapSlice';
import { useEffect, useState } from 'react';
import { toRouteRows } from './RoutesTableConfiguration';
import { routeColour } from './RouteColours';
import ResultMap from './ResultMap';
import {
  StopRow,
  stopsTableModel,
  toStopRows,
} from './StopsTableConfiguration';

export interface DriverRouteDialogProps {
  /**
   * The selected Planning Depot's routes in display order, every Cluster
   * merged into one list; the index is both the driver number and the route's
   * identity, because vehicle ids repeat across a depot's Solves.
   */
  routes: PlannedRouteView[];
  /** Open when this is a route's index. */
  routeIndex: number | null;
  depotLatitude: number | null | undefined;
  depotLongitude: number | null | undefined;
  /** Steps to the previous or the next driver, without closing the dialog. */
  onSelectRoute: (routeIndex: number) => void;
  onClose: () => void;
}

const subtleText = 'text-light-black dark:text-sublte-white';

const startStepType = 'start';

const stopZoomLevel = 16;

/** "leaves depot 08:00", or nothing when the route has no start step. */
const departureOf = (route: PlannedRouteView) => {
  const start = route.steps.find((step) => step.type === startStepType);
  return start ? secondsFromMidnightToHhMm(start.arrivalSeconds) : null;
};

/**
 * One driver's route, full width: the map on the left, the stops on the right.
 * Compliance: like the stops table it is built on, this shows the file's own
 * DeliveryIds and never customer name, address or phone.
 */
const DriverRouteDialog = ({
  routes,
  routeIndex,
  depotLatitude,
  depotLongitude,
  onSelectRoute,
  onClose,
}: DriverRouteDialogProps) => {
  const dispatch = useAppDispatch();
  const [focusedStopSequence, setFocusedStopSequence] = useState<number | null>(
    null,
  );

  useEffect(() => {
    setFocusedStopSequence(null);
  }, [routeIndex]);

  const index = routeIndex ?? -1;
  const route = routes[index];

  if (!route) {
    return null;
  }

  // Reuses the routes table's own numbers, so the header and the row agree.
  const summary = toRouteRows(routes)[index];
  const stopRows = toStopRows(route);
  const departure = departureOf(route);
  const hasPrevious = index > 0;
  const hasNext = index < routes.length - 1;

  const focusStop = (stop: StopRow) => {
    if (stop.latitude == null || stop.longitude == null) {
      return;
    }
    setFocusedStopSequence(stop.sequence);
    dispatch(
      setCoordinates({ latitude: stop.latitude, longitude: stop.longitude }),
    );
    dispatch(setZoomLevel(stopZoomLevel));
  };

  return (
    <DesignDialog
      open
      onClose={onClose}
      labelledBy="driverRouteDialogTitle"
      className="h-[780px] w-[1280px]"
    >
      <div className="flex h-16 items-center gap-4 border-b border-design-secondary-black px-5">
        <span
          aria-hidden="true"
          className="h-3.5 w-3.5 rounded-sm"
          style={{ backgroundColor: routeColour(index) }}
        />
        <h3 id="driverRouteDialogTitle" className="text-xl font-bold">
          Driver {summary.driverNumber}
        </h3>
        <span className={`text-sm ${subtleText}`}>
          {summary.stops} stops · {summary.deliveries} deliveries ·{' '}
          {(summary.distanceMeters / 1000).toFixed(1)} km ·{' '}
          {formatSecondsAsHoursMinutes(summary.durationSeconds)} driving ·{' '}
          {formatSecondsAsHoursMinutes(summary.serviceSeconds)} service · back
          at depot {summary.endTime}
        </span>
        <span className="flex-grow" />
        <div className="flex items-center gap-2">
          <DesignButton
            variant="secondary"
            size="sm"
            disabled={!hasPrevious}
            ariaLabel="Previous driver"
            onClickHandler={
              hasPrevious ? () => onSelectRoute(index - 1) : undefined
            }
          >
            <ChevronLeftIcon className="h-4 w-4" />
            {hasPrevious ? `Driver ${index}` : 'Previous'}
          </DesignButton>
          <DesignButton
            variant="secondary"
            size="sm"
            disabled={!hasNext}
            ariaLabel="Next driver"
            onClickHandler={hasNext ? () => onSelectRoute(index + 1) : undefined}
          >
            {hasNext ? `Driver ${index + 2}` : 'Next'}
            <ChevronRightIcon className="h-4 w-4" />
          </DesignButton>
          <span
            aria-hidden="true"
            className="h-6 w-px bg-design-secondary-black"
          />
          <DesignButton
            variant="ghost"
            ariaLabel="Close"
            title="Close (Esc)"
            className="h-11 w-11 px-0"
            onClickHandler={onClose}
          >
            <CloseIcon />
          </DesignButton>
        </div>
      </div>

      <div className="flex min-h-0 flex-grow">
        <div className="flex-grow border-r border-design-secondary-black">
          <ResultMap
            id="capacityPlanDriverMap"
            routes={routes}
            depotLatitude={depotLatitude}
            depotLongitude={depotLongitude}
            selectedRouteIndex={index}
            focusedStopSequence={focusedStopSequence}
          />
        </div>
        <div className="flex min-h-0 w-[600px] flex-col">
          <div className="flex items-center justify-between border-b border-design-secondary-black px-4 py-3">
            <h3 className="text-base font-bold">Stops</h3>
            {departure && (
              <span className={`text-sm ${subtleText}`}>
                leaves depot {departure}
              </span>
            )}
          </div>
          <div className="min-h-0 flex-grow overflow-y-auto">
            {stopRows.length === 0 ? (
              <p className="p-4 text-sm">This driver has no stops.</p>
            ) : (
              <GenericTable
                framed
                model={stopsTableModel}
                elements={stopRows}
                pageNumber={1}
                totalElements={1}
                showPaging={false}
                pageChanged={() => {}}
                onRowClick={focusStop}
                rowClassName={(stop) =>
                  stop.sequence === focusedStopSequence
                    ? 'bg-light-gray dark:bg-design-secondary-black'
                    : ''
                }
              />
            )}
          </div>
        </div>
      </div>
    </DesignDialog>
  );
};

export default DriverRouteDialog;
